import { RefObject, useEffect, useMemo, useState } from 'react';

export default function useOnScreen(ref: RefObject<HTMLElement | null>) {
  const [isIntersecting, setIntersecting] = useState(false);

  const observer = useMemo(
    () => new IntersectionObserver(([entry]) => setIntersecting(entry.isIntersecting)),
    [],
  );

  useEffect(() => {
    if (ref.current) {
      observer.observe(ref.current);
      return () => observer.disconnect();
    }
  }, [observer, ref]);

  return isIntersecting;
}

/**
 * Returns `true` once the element has stayed in view for `delayMs`, then latches.
 * Elements that only flash through the viewport during a fast scroll never reach
 * the delay, so heavy children mount only where the scroll settles. The latch
 * (it never flips back to false) avoids unmount/remount churn while the element
 * remains rendered.
 */
export function useDeferredOnScreen(ref: RefObject<HTMLElement>, delayMs = 120): boolean {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || ready) return;

    let timer: ReturnType<typeof setTimeout> | undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        timer = setTimeout(() => setReady(true), delayMs);
      } else if (timer) {
        clearTimeout(timer);
        timer = undefined;
      }
    });
    observer.observe(el);

    return () => {
      if (timer) clearTimeout(timer);
      observer.disconnect();
    };
  }, [ref, delayMs, ready]);

  return ready;
}
